// Fake `hostData` — pretends to be the host product's read-only data window.
// The SHAPES below are the proposed contract between Jigyasa and the host (Kreta).
// Buttons 1, 2, 3, 5, 7 are host-side. Button 4 (past enquiries) is Jigyasa-side and lives in state.js.
// Returns are wrapped in Promises so the real host can be async without changing UI code.

const today = new Date();
const daysFromNow = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};
const iso = today.toISOString();

// ---------- Button 1 — liveStock() ----------
// Proposed shape: industry-neutral. Coworking calls a "unit" a "seat" and a "location" a "hub".
const LIVE_STOCK = {
  units: "seat",
  asOf: iso,
  totalCapacity: 782,
  totalOccupied: 571,
  totalFree: 211,
  locations: [
    {
      locationId: "evolve",
      locationName: "Evolve Hub",
      totalCapacity: 166, occupied: 166, free: 0, occupancyPct: 100,
      productBreakdown: { corporate: { capacity: 166, occupied: 166, free: 0 }, dayPass: { capacity: 0, occupied: 0, free: 0 } },
      occupants: [
        { customerId: "c-alpha", customerName: "AlphaSoft", unitCount: 60, subLocation: "Cabin A" },
        { customerId: "c-beta", customerName: "Beta Studios", unitCount: 40, subLocation: "Cabin B" },
        { customerId: "c-gamma", customerName: "Gamma Labs", unitCount: 66, subLocation: "Open floor" },
      ],
    },
    {
      locationId: "landmark",
      locationName: "Landmark Hub",
      totalCapacity: 71, occupied: 25, free: 46, occupancyPct: 35,
      productBreakdown: { corporate: { capacity: 71, occupied: 25, free: 46 }, dayPass: { capacity: 0, occupied: 0, free: 0 } },
      occupants: [
        { customerId: "c-delta", customerName: "DeltaCorp", unitCount: 25, subLocation: "Cabin C" },
      ],
    },
    {
      locationId: "midway",
      locationName: "Midway Hub",
      totalCapacity: 86, occupied: 53, free: 33, occupancyPct: 62,
      productBreakdown: { corporate: { capacity: 86, occupied: 53, free: 33 }, dayPass: { capacity: 0, occupied: 0, free: 0 } },
      occupants: [
        { customerId: "c-epsilon", customerName: "Epsilon Inc", unitCount: 53, subLocation: "Open floor" },
      ],
    },
    {
      locationId: "navarang",
      locationName: "Navarang Hub",
      totalCapacity: 157, occupied: 95, free: 62, occupancyPct: 60,
      productBreakdown: { corporate: { capacity: 157, occupied: 95, free: 62 }, dayPass: { capacity: 0, occupied: 0, free: 0 } },
      occupants: [
        { customerId: "c-zeta", customerName: "Zeta Holdings", unitCount: 35, subLocation: "Cabin D" },
        { customerId: "c-techco", customerName: "TechCo", unitCount: 60, subLocation: "Open floor" },
      ],
    },
    {
      locationId: "vijay",
      locationName: "Vijay Hub",
      totalCapacity: 338, occupied: 268, free: 70, occupancyPct: 79,
      productBreakdown: { corporate: { capacity: 338, occupied: 268, free: 70 }, dayPass: { capacity: 0, occupied: 0, free: 0 } },
      occupants: [
        { customerId: "c-techco", customerName: "TechCo", unitCount: 8, subLocation: "Cabin A" },
        { customerId: "c-eta", customerName: "Eta Solutions", unitCount: 120, subLocation: "Open floor" },
        { customerId: "c-theta", customerName: "Theta Group", unitCount: 80, subLocation: "Cabin B" },
        { customerId: "c-iota", customerName: "Iota Ventures", unitCount: 60, subLocation: "Cabin C" },
      ],
    },
  ],
};

// ---------- Button 2 — upcomingStock(windowDays) ----------
const UPCOMING_STOCK_EVENTS = [
  { type: "vacancy", locationId: "midway", locationName: "Midway Hub", subLocation: "Cabin E", effectiveDate: daysFromNow(8), unitCount: 5, currentOccupant: "MicroLabs", note: "Lease expiring, not renewing." },
  { type: "vacancy", locationId: "vijay", locationName: "Vijay Hub", subLocation: "Cabin D", effectiveDate: daysFromNow(22), unitCount: 12, currentOccupant: "NorthStar Pvt Ltd", note: "Downsizing — moving to a smaller cabin." },
  { type: "vacancy", locationId: "landmark", locationName: "Landmark Hub", subLocation: "Open floor", effectiveDate: daysFromNow(35), unitCount: 8, currentOccupant: "PixelForge", note: null },
  { type: "vacancy", locationId: "navarang", locationName: "Navarang Hub", subLocation: "Cabin D", effectiveDate: daysFromNow(48), unitCount: 35, currentOccupant: "Zeta Holdings", note: "Relocating to bigger space at Vijay." },
];

// ---------- Button 3 — customerFootprint(contactKey) ----------
// Looked up by phone, email, or company. Demo: phone match drives lookups.
const FOOTPRINT_BY_PHONE = {
  "09889012345": {
    hostCustomerId: "c-techco",
    customerName: "TechCo",
    primaryContactPhone: "09889012345",
    primaryContactName: "Arjun Nair",
    since: "2024-03-12",
    plan: "Cabin A · 8 seats · Vijay Hub",
    monthlyValue: 85000,
    units: [
      { locationId: "vijay", locationName: "Vijay Hub", subLocation: "Cabin A", unitCount: 8, since: "2024-03-12", leaseEnd: daysFromNow(35), productKey: "corporate" },
      { locationId: "navarang", locationName: "Navarang Hub", subLocation: "Open floor", unitCount: 60, since: "2024-06-01", leaseEnd: daysFromNow(110), productKey: "corporate" },
    ],
    healthScore: 8,
    openComplaints: 0,
    notes: "Long-time client. Original 8-seat cabin at Vijay plus a 60-seat expansion at Navarang.",
  },
};

// ---------- Button 5 — pricingAndOffers(productKey) ----------
const PRICING_BY_TYPE = {
  "Single seat": {
    standardRate: 8500, // per seat per month
    bulkDiscountAvailable: false,
    activeOffers: [
      { offerCode: "WELCOME10", label: "10% off first 3 months for new sign-ups", validTill: daysFromNow(45), eligibilityNote: "New clients only" },
    ],
    recentCloses: [
      { closedAt: daysFromNow(-14), seatCount: 1, monthlyValue: 8000, locationName: "Vijay Hub" },
      { closedAt: daysFromNow(-22), seatCount: 1, monthlyValue: 8500, locationName: "Landmark Hub" },
    ],
    operatorDiscountLimitPct: 10,
  },
  "Open seats (team)": {
    standardRate: 7500,
    bulkDiscountAvailable: true,
    activeOffers: [
      { offerCode: "TEAM12", label: "5% off on 12-month commits for teams of 5+", validTill: daysFromNow(60), eligibilityNote: "Teams of 5 seats or more" },
    ],
    recentCloses: [
      { closedAt: daysFromNow(-5), seatCount: 5, monthlyValue: 35000, locationName: "Vijay Hub" },
      { closedAt: daysFromNow(-19), seatCount: 4, monthlyValue: 28000, locationName: "Midway Hub" },
      { closedAt: daysFromNow(-30), seatCount: 8, monthlyValue: 55000, locationName: "Vijay Hub" },
    ],
    operatorDiscountLimitPct: 12,
  },
  "Cabin": {
    standardRate: 7000, // per seat per month inside a cabin
    bulkDiscountAvailable: true,
    activeOffers: [
      { offerCode: "CAB12", label: "8% off on 12-month commits for cabins", validTill: daysFromNow(75), eligibilityNote: "Cabins only, min 4 seats" },
    ],
    recentCloses: [
      { closedAt: daysFromNow(-9), seatCount: 6, monthlyValue: 42000, locationName: "Vijay Hub" },
      { closedAt: daysFromNow(-21), seatCount: 8, monthlyValue: 56000, locationName: "Navarang Hub" },
    ],
    operatorDiscountLimitPct: 15,
  },
};

// ---------- Button 7 — renewalPipeline(windowDays) ----------
const RENEWAL_PIPELINE = [
  {
    hostCustomerId: "c-techco",
    customerName: "TechCo",
    primaryContactPhone: "09889012345",
    primaryContactName: "Arjun Nair",
    leaseEnd: daysFromNow(35),
    daysUntilEnd: 35,
    currentPlan: "Cabin A · 8 seats · Vijay Hub",
    monthlyValue: 85000,
    growthSignal: { score: 1, narrative: "Added 2 guest visitors/week for the last month; pass usage up 40%." },
    healthScore: 8,
    lastRenewedAt: "2024-03-12",
  },
  {
    hostCustomerId: "c-epsilon",
    customerName: "Epsilon Inc",
    primaryContactPhone: "09812341111",
    primaryContactName: "Reena Iyer",
    leaseEnd: daysFromNow(18),
    daysUntilEnd: 18,
    currentPlan: "Open floor · 53 seats · Midway Hub",
    monthlyValue: 397500,
    growthSignal: { score: -1, narrative: "Pass usage down 30% over 6 weeks; 2 support tickets unresolved." },
    healthScore: 4,
    lastRenewedAt: "2024-12-18",
  },
  {
    hostCustomerId: "c-iota",
    customerName: "Iota Ventures",
    primaryContactPhone: "09823459999",
    primaryContactName: "Sandeep Rao",
    leaseEnd: daysFromNow(52),
    daysUntilEnd: 52,
    currentPlan: "Cabin C · 60 seats · Vijay Hub",
    monthlyValue: 420000,
    growthSignal: { score: 0, narrative: "Usage steady." },
    healthScore: 7,
    lastRenewedAt: "2024-08-01",
  },
];

// ---------- Public API ----------

const wait = (ms = 50) => new Promise((r) => setTimeout(r, ms));

export const hostData = {
  liveStock: async () => {
    await wait();
    return LIVE_STOCK;
  },

  upcomingStock: async ({ windowDays = 60 } = {}) => {
    await wait();
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() + windowDays);
    const events = UPCOMING_STOCK_EVENTS.filter(
      (e) => new Date(e.effectiveDate) <= cutoff
    );
    return { windowDays, asOf: iso, events };
  },

  customerFootprint: async ({ phone, email, company, hostCustomerId } = {}) => {
    await wait();
    let match = null;
    let matchedOn = null;
    const normalize = (s) => String(s || "").replace(/\s*\(DEMO\)\s*$/i, "").toLowerCase().trim();
    // Phones compared on normalised 10-digit form (handles "0"-prefixed
    // stored values and raw 10-digit input from the live form lookup).
    const normPhone = (p) => String(p || "").replace(/\D/g, "").replace(/^91/, "").replace(/^0+/, "");
    if (phone) {
      const target = normPhone(phone);
      const foundKey = Object.keys(FOOTPRINT_BY_PHONE).find((k) => normPhone(k) === target);
      if (foundKey) {
        match = FOOTPRINT_BY_PHONE[foundKey];
        matchedOn = "phone";
      }
    }
    if (!match && company) {
      const target = normalize(company).replace(/\s+(pvt|private|limited|ltd)\.?/g, "").trim();
      const found = Object.values(FOOTPRINT_BY_PHONE).find((c) => {
        const candidate = normalize(c.customerName).replace(/\s+(pvt|private|limited|ltd)\.?/g, "").trim();
        return candidate === target || target.includes(candidate) || candidate.includes(target);
      });
      if (found) {
        match = found;
        matchedOn = "company";
      }
    }
    return { matched: !!match, matchedOn, customer: match };
  },

  pricingAndOffers: async ({ enquiryType } = {}) => {
    await wait();
    return PRICING_BY_TYPE[enquiryType] || null;
  },

  renewalPipeline: async ({ windowDays = 60 } = {}) => {
    await wait();
    const filtered = RENEWAL_PIPELINE.filter((r) => r.daysUntilEnd <= windowDays);
    return { windowDays, asOf: iso, renewals: filtered };
  },
};

// ---------- Costume-aware capacity helper ----------
// Given a seat-count ask, returns a plain-English capacity recommendation.
export const computeCapacityFit = (stock, askedUnits) => {
  if (!stock || !askedUnits || askedUnits <= 0) return null;
  const sorted = [...stock.locations].sort((a, b) => b.free - a.free);
  const singleFit = sorted.find((l) => l.free >= askedUnits);

  // No single-hub fit — pick best partial + suggest a phased split
  if (!singleFit) {
    const best = sorted[0];
    const remaining = askedUnits - best.free;
    const second = sorted.find((l) => l.locationId !== best.locationId && l.free > 0);
    return {
      fits: false,
      askedUnits,
      bestPartial: best,
      remaining,
      phasedSecond: second && second.free >= remaining ? second : null,
      allLocations: stock.locations.map((l) => ({ name: l.locationName, free: l.free })),
    };
  }
  return {
    fits: true,
    askedUnits,
    fitLocation: singleFit,
    allLocations: stock.locations.map((l) => ({ name: l.locationName, free: l.free })),
  };
};
